import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getAppParams, getUrlParams } from "../apiService/timespam";
import "./Theme.css";
import "./GamesForGood.css";
import { APIV4_BASE_URL } from "../apiService/apiClient";

const CIRCLE_CIRCUMFERENCE = 119.38;
const GAME_DURATIONS = [65, 35, 50, 50, 50]; // estimated seconds per game; falls back to 60s for extras

/* ---- Game progress navigation ---- */
const GameNavigation = ({ games, currentGameIndex }) => {
  const [progress, setProgress] = useState(() => games.map(() => CIRCLE_CIRCUMFERENCE));

  useEffect(() => {
    if (currentGameIndex >= games.length) return;
    const duration = GAME_DURATIONS[currentGameIndex] || 60;
    let elapsed = 0;

    // Reset current ring to full (hidden)
    setProgress((prev) => {
      const next = [...prev];
      next[currentGameIndex] = CIRCLE_CIRCUMFERENCE;
      return next;
    });

    const timer = setInterval(() => {
      elapsed += 1;
      const pct = elapsed / duration;
      setProgress((prev) => {
        const next = [...prev];
        next[currentGameIndex] = CIRCLE_CIRCUMFERENCE * (1 - Math.min(pct, 1));
        return next;
      });
      if (elapsed >= duration) clearInterval(timer);
    }, 1000);

    return () => clearInterval(timer);
  }, [currentGameIndex, games.length]);

  return (
    <div className="gfg-nav-container">
      <div className="gfg-nav-wrapper">
        {games.map((game, i) => (
          <div key={i} className="gfg-nav-item">
            <div className="gfg-nav-circle">
              <img
                src={
                  i < currentGameIndex
                    ? "https://storage.googleapis.com/ksabupatest/2025/01/29/52y4z/hjs9l94u7a.png"
                    : "https://storage.googleapis.com/ksabupatest/2025/01/29/meqaj/v2t2blzjd8.png"
                }
                alt={i < currentGameIndex ? "completed" : "pending"}
                className="gfg-nav-icon"
              />
              {i === currentGameIndex && (
                <svg className="gfg-progress-circle" viewBox="-1 -1 42 42">
                  <circle className="gfg-progress-bg" cx="20" cy="20" r="19" />
                  <circle
                    className="gfg-progress-ring"
                    cx="20"
                    cy="20"
                    r="19"
                    style={{ strokeDashoffset: progress[i] }}
                  />
                </svg>
              )}
            </div>
            <span className="gfg-nav-label">Game #{i + 1}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---- Main page ---- */
const AllGamesPlay = () => {
  const navigate = useNavigate();
  const { theme, lang } = getUrlParams();
  const { uid, goqiiUserId, nonce, apiKey, signature, goqiiAccessToken, appVersion, appType } = getAppParams();

  useEffect(() => {
    document.documentElement.className = theme === "dark" ? "dark" : "";
    if (lang === "ar") document.documentElement.dir = "rtl";
  }, [theme, lang]);

  const [games, setGames] = useState([]);
  const [currentGameIndex, setCurrentGameIndex] = useState(0);
  const [currentGroupName, setCurrentGroupName] = useState("");

  const qs = window.location.search;
  const gameMode = new URLSearchParams(qs).get("gameMode");

  // Build the post-game redirect URL: /games/:uid with banner flag preserved
  const gamesRedirectQs = (() => {
    const p = new URLSearchParams(qs);
    p.set("showBanner", "1");
    return `?${p.toString()}`;
  })();

  const buildBody = useCallback(
    (extra = {}) =>
      new URLSearchParams({
        goqiiUserId, nonce, apiKey, signature, goqiiAccessToken, appVersion, appType,
        userId: uid,
        ...(gameMode ? { gameMode } : {}),
        ...extra,
      }).toString(),
    [goqiiUserId, nonce, apiKey, signature, goqiiAccessToken, appVersion, appType, uid, gameMode]
  );

  const post = useCallback(
    async (endpoint, extra = {}) => {
      const res = await fetch(`${APIV4_BASE_URL}/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: buildBody(extra),
      });
      return res.json();
    },
    [buildBody]
  );

  // Fetch the games list on mount
  useEffect(() => {
    const loadGames = async () => {
      try {
        const json = await post("games/fetch_games");
        if (json.code === 200) {
          const rawGames = json.data.games || [];
          const processed = rawGames.map((g) => {
            try {
              const url = new URL(g.url);
              if (gameMode) {
                url.searchParams.set("gameMode", gameMode);
                url.searchParams.set("groupName", g.groupName);
              }
              return { ...g, url: url.toString() };
            } catch {
              return g;
            }
          });
          if (gameMode && rawGames[0]) setCurrentGroupName(rawGames[0].groupName);
          setGames(processed);
        }
      } catch (e) {
        console.error(e);
      }
    };
    loadGames();
  }, [post, gameMode]);

  // ── postMessage listener ──────────────────────────────────────────────────
  // New in-app games (served by goqii-lifestyle itself) call
  //   window.parent.postMessage({ type: 'GOQII_GAME_COMPLETE' }, '*')
  // from ResultPage when the result screen appears.
  // This is the only reliable completion signal for those games because they
  // use a different backend service (lifestylecard/save_game_score) that does
  // NOT update the cognitive_game_status endpoint the polling below uses.
  useEffect(() => {
    if (!games.length) return;

    const handleMessage = (e) => {
      if (e.data?.type !== 'GOQII_GAME_COMPLETE') return;
      setCurrentGameIndex((prev) => prev + 1);
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [games.length]);

  // ── Poll game status every 5 s (old external games) ──────────────────────
  // Old games that run on gamesforgood.goqii.com update cognitive_game_status
  // on the backend when they finish; the poll picks that up.
  // New in-app games are handled above via postMessage instead.
  useEffect(() => {
    if (!games.length) return;

    if (currentGameIndex >= games.length) {
      navigate(`/games/${uid}${gamesRedirectQs}`);
      return;
    }

    const interval = setInterval(async () => {
      try {
        const json = await post("games/cognitive_game_status", {
          gid: games[currentGameIndex].gid,
          groupName: currentGroupName,
        });
        if (json.data?.currentGameCompleted) {
          clearInterval(interval);
          setCurrentGameIndex((p) => p + 1);
        }
      } catch (e) {
        console.error(e);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [games, currentGameIndex, currentGroupName, post, navigate, qs]);

  const currentGame = games[currentGameIndex];

  return (
    <div className="gfg-play-wrapper">
      <GameNavigation games={games} currentGameIndex={currentGameIndex} />

      {currentGame && (
        <div className="gfg-play-container">
          <p className="gfg-play-heading">{currentGame.category}</p>
          <iframe
            src={currentGame.url}
            title={`Game ${currentGame.gid}`}
            className="gfg-play-iframe"
            allow="autoplay; fullscreen; accelerometer; gyroscope; camera; microphone"
          />
        </div>
      )}
    </div>
  );
};

export default AllGamesPlay;
